import { useRemountFileInput } from '../../hooks/useRemountFileInput';
import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Transition } from '../../components/Transition';
import { NumericFormatCustom } from '../../components/NumericCustomFormatter';
import { DocumentType } from '../../enum';
import { uploadAssets } from '../../utils/uploadAssets';
import { getAssetURl } from '../../utils/url';
import { useAppSelector } from '../../store';

interface IProps {
  toggle: boolean;
  onClose: () => void;
  values: any;
  handleSubmit: () => void;
  setFieldValue: (key: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
  errors: any;
  touched: any;
  isSubmitting: boolean;
  resetForm: () => void;
}

const CakeBoxFormModal: React.FC<IProps> = ({
  toggle,
  onClose,
  errors,
  touched,
  values,
  isSubmitting,
  setFieldValue,
  handleChange,
  handleSubmit,
  resetForm,
}) => {
  const { fileInputRef, fileInputKey, clearFileInput } = useRemountFileInput(
    toggle,
    values?.id,
    Boolean(values?.imgUrl),
    'imgUrl'
  );
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files?.[0]) {
      const uploadResult: any = await uploadAssets(
        event.target.files?.[0],
        DocumentType.CakeBox,
        accessToken
      );
      if (uploadResult?.data?.id) {
        setFieldValue('imgUrl', uploadResult?.data?.keypath);
      }
    }
  };

  return (
    <Dialog
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => {
        resetForm();
        onClose();
      }}
      aria-describedby='alert-dialog-slide-description'
    >
      <DialogTitle sx={{ mt: -0.5, mb: -0.5 }}>Cake Box</DialogTitle>
      <form
        onSubmit={handleSubmit}
        autoComplete='off'
      >
        <DialogContent dividers>
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                id='labelEn'
                name='labelEn'
                label='English'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelEn}
                error={Boolean(errors.labelEn && touched.labelEn)}
                helperText={
                  Boolean(errors.labelEn && touched.labelEn) && errors.labelEn
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                id='labelAr'
                name='labelAr'
                label='Arabic'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.labelAr}
                error={Boolean(errors.labelAr && touched.labelAr)}
                helperText={
                  Boolean(errors.labelAr && touched.labelAr) && errors.labelAr
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                id='price'
                name='price'
                label='Price (QAR)'
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.price}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                }}
                error={Boolean(errors.price && touched.price)}
                helperText={
                  Boolean(errors.price && touched.price) && errors.price
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <TextField
                id='position'
                name='position'
                label={'Position'}
                fullWidth
                variant='outlined'
                onChange={handleChange}
                value={values.position}
                InputProps={{
                  inputComponent: NumericFormatCustom as any,
                }}
                error={Boolean(errors.position && touched.position)}
                helperText={
                  Boolean(errors.position && touched.position) &&
                  errors.position
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                }}
              >
                <TextField
                  key={fileInputKey}
                  type='file'
                  fullWidth
                  name='imgUrl'
                  id='imgUrl'
                  inputRef={fileInputRef}
                  onChange={handleFileChange}
                  inputProps={{ multiple: false, accept: 'image/*' }}
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  label={'Image'}
                />
                {values?.imgUrl ? (
                  <Box
                    sx={{
                      position: 'relative',
                      width: { xs: '75px', sm: '100px' },
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    <IconButton
                      size='small'
                      aria-label='remove image'
                      onClick={() => {
                        setFieldValue('imgUrl', '');
                        clearFileInput();
                      }}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          bgcolor: 'grey.100',
                        },
                      }}
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                    <Avatar
                      imgProps={{ crossOrigin: 'anonymous' }}
                      src={getAssetURl(values.imgUrl, assetUrl)}
                      sx={{
                        borderRadius: 'unset',
                        objectFit: 'contain',
                        '& img': { objectFit: 'contain' },
                        width: '100%',
                        height: 50,
                      }}
                    />
                  </Box>
                ) : null}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    color='primary'
                    name='activeState'
                    onChange={(e) =>
                      setFieldValue('activeState', e.target.checked)
                    }
                    checked={values.activeState}
                  />
                }
                label={'Active Status'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              resetForm();
              onClose();
            }}
            variant='outlined'
          >
            {'Cancel'}
          </Button>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            variant='contained'
          >
            {'Save'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CakeBoxFormModal;
